// See: https://codesandbox.io/s/github/reduxjs/redux-toolkit/tree/master/examples/query/react/kitchen-sink
// Import the RTK Query methods from the React-specific entry point
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { APP_ROUTES } from '@constants/constants';
import { HYDRATE } from '@/utils/store-wrapper';
import urlsService from '@/services/urls-service';
import { appInsights } from '@/telemetry/client';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';

const baseQuery = fetchBaseQuery({
  baseUrl: typeof window === 'undefined' ? urlsService.getBaseURL() : '',
  prepareHeaders: (headers, { extra }: { extra: any }) => {
    if (typeof window !== 'undefined') {
      return headers;
    }

    const cookies = extra?.cookies;
    if (cookies) {
      const token = cookies['cl_token'] ?? null;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      // Explicitly forward cookies (in SSR, they aren't forwarded automatically)

      // Convert the cookies object into a single cookie string
      const cookieString = Object.entries(cookies)
        .map(([key, value]) => `${key}=${value}`)
        .join('; ');

      headers.set('cookie', cookieString);
    }

    return headers;
  }
});

export const baseQueryWithAuth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error?.status === 401 && typeof window !== 'undefined') {
    // If any response is 401 Unauthorized, redirect to the login route

    const isLoginRoute = window.location.href.indexOf(APP_ROUTES.LOGIN) !== -1;
    const isShoppingCartRoute = window.location.href.indexOf(APP_ROUTES.SHOPPINGCART) !== -1;
    const isCheckoutRoute = window.location.href.indexOf(APP_ROUTES.CHECKOUT) !== -1;

    const redirectUrl = `${APP_ROUTES.LOGIN}?returnUrl=${encodeURIComponent(document.URL)}`;
    // eslint-disable-next-line prettier/prettier
    (!isLoginRoute && !isShoppingCartRoute && !isCheckoutRoute) && window.location.replace(redirectUrl);
    return Promise.reject();
  }
  if (result.error) {
    appInsights.trackException({ exception: result.error as any });
  }

  return result;
};

/* eslint-disable prettier/prettier */
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithAuth,
  extractRehydrationInfo(action, { reducerPath }) {
    if (action.type === HYDRATE) {
      return action.payload[reducerPath];
    }
  },
  // Keeps TS from throwing errors
  tagTypes: [
      'CURRENT_USER',
      'CART',
      'delivery-addresses',
      'order-lists',
      'returns',
      'invoices',
      'order-history',
      'CATEGORIES',
      'CHECKOUT',
      'QUOTES'
  ],
  // Endpoints are injected by other slices. This is here to stop TS complaining
  endpoints: () => ({})
});
/* eslint-enable prettier/prettier */

export const {
  util: { getRunningQueriesThunk }
} = baseApi;
